import React, { useEffect, useRef, useState, useContext } from 'react';
import { loadScript } from '@buzzfeed/bf-utils/lib/load-script';
import { queryStringToObject } from '@buzzfeed/bf-utils/lib/query-string';
import { AdsContext } from '@buzzfeed/adlib/dist/module/bindings/react/contexts';
import { getNumArray } from '@buzzfeed/adlib/dist/module/bindings/react';
import createInlinePlacer from '@buzzfeed/adlib/dist/module/bindings/react/managers/InlinePlacer';
import createQuizInlinePlacer from '@buzzfeed/adlib/dist/module/bindings/react/managers/QuizInlinePlacer';
import createBFPQuizManager from '@buzzfeed/adlib/dist/module/bindings/react/managers/BFPQuizManager';
import createBFPCardQuizManager from '@buzzfeed/adlib/dist/module/bindings/react/managers/BFPCardQuizManager';
import { isMobile } from '@buzzfeed/bf-utils/lib/device';
import { useT3 } from '../../hooks/useT3';
import ABeagleContext from '../../contexts/abeagle';
import BuzzContext from '../../contexts/buzz';
import AdUnit from '../Ads/AdUnit';
import { isNews } from '../../utils/isNews';
import { isGamesPost } from '../../utils/games';
import { isOrHasQuiz } from '../../utils/isOrHasQuiz';
import { isServer } from '../../utils/isServer';
import { CLUSTER } from '../../constants';
import InlinePromoUnit   from '../InlinePromo';
// import { getReferrer } from '@buzzfeed/bf-utils/lib/referrer';
import ElementsPlayer from '../Connatix/Elements';
import { useCardStyleAdEligibility } from '../../hooks/useCardStyleAdEligibility';
import { useGameInterstitialAd } from '../../hooks/useGameInterstitialAd';

// RT-1920: check if traffic is from a paid campaign origin (arbitrage or fsbfdc)
function isPaidCampaignOrigin() {
  if (typeof window === 'undefined') return false;
  const queryParams = queryStringToObject(window.location.search);
  return !!queryParams['origin']?.includes('sdxa') || queryParams['origin'] === 'fsbfdc';
}

// list all BFPs which use `bfp-ad-placeholders-ready` message to broadcast status of ads placeholders
const BFP_WITH_ADS = ['ai_quiz', 'scale_quiz', 'tap_on_image', 'quiz', 'ai_generator', 'word_chain'];
const WORD_CHAIN_AD_PLACEHOLDERS_READY = 'bfp-word-chain-ad-placeholders-ready';

const withInlineAds = Component => {
  return function ComponentWithInlineAds(props) {
    const { buzz = {} } = useContext(BuzzContext);
    const adsContext = useContext(AdsContext);
    const userCountry = adsContext?.env?.userCountry;
    const isNZorAU = userCountry === 'au' || userCountry === 'nz';
    const containerRef = useRef(null);
    const bfpQuizPlacer = useRef(null);
    const [adPortals, setAdPortals] = useState([]);
    const t3App = useT3();
    const pixiedust = props ? props.pixiedust : {};
    const hideAds = props?.hideAds || false;

    const {
      experiments,
      getExperimentValue
    } = useContext(ABeagleContext);

    const {
      handleBroadcast: handleGameInterstitialBroadcast,
    } = useGameInterstitialAd();

    const {
      isInlineAdEligible,
      isInterstitialAdEligible,
      isInlineAndInterstitialAdEligible
    } = useCardStyleAdEligibility(buzz);
    const isEnableInlineAdCardQuiz = isInlineAdEligible || isInlineAndInterstitialAdEligible;
    const isEnabledInterstitialAdCardQuiz = isInterstitialAdEligible || isInlineAndInterstitialAdEligible;

    const disablePromo1Native = (buzz.isQuiz ||
      buzz.isBFPQuiz ||
      buzz.sub_buzzes?.find((sb) => sb.form === 'quiz')) &&
      !isServer() && isMobile();

    const highDensityNonQuiz = (
      !(
        buzz.isQuiz ||
        buzz.isShopping ||
        buzz.isBFPQuiz ||
        buzz.sub_buzzes?.find((sb) => sb.form === 'quiz')
      ) && buzz.classification.edition !== 'Japan' && !buzz.isAd
    );
    // RT-1920: high density ad injection logic for paid campaigns
    const highDensityForCampaign = isPaidCampaignOrigin();

    const highDensityQuiz = (
      (
        buzz.isQuiz ||
        buzz.isBFPQuiz ||
        buzz.sub_buzzes?.find((sb) => sb.form === 'quiz')
      ) && buzz.classification.edition !== 'Japan' && !buzz.isAd
    );

    let rt1520IncreaseDensity = 'control';
    try {
      rt1520IncreaseDensity = getExperimentValue(
        'RT-1520-density',
      );
    } catch(e) {
      // there's a specific error, but any error we wanna catch
      rt1520IncreaseDensity = 'control';
    }

    const [bfpAdsMessages, setBfpAdsMessages] = useState([]);
    const [bfpLoadIntervalFinished, setBfpLoadIntervalFinished] = useState(false);

    /**
     * MISC/UK LEGACY QUIZ INLINE PLACEMENT
     */
    useEffect(() => {
      setAdPortals([]);
      bfpQuizPlacer.current = null;
    }, [buzz.id]);

    useEffect(
      () => {
        let bfpMessages = [];
        let weightedQuizMessages = [];

        const onBfpT3Message = ({ data }) => {
          // Delegate to unified game interstitial handler (word chain, huedoku, etc.)
          const gameAction = handleGameInterstitialBroadcast(data.message, data.messageData);
          if (gameAction === 'trigger') {
            bfpQuizPlacer.current?.onGameInterstitialReady();
          }

          if (data.message === 'ukEngineQuiz_Ready' && data.messageData) {
            weightedQuizMessages = getNumArray(
              data.messageData.totalQuestions,
              0
            ).map(i => `bfp-ad-placeholder-${i}-ready`);
          } else if (
            data.message === 'bfp-ad-placeholders-ready' ||
            data.message === 'bfp-card-quiz-ad-placeholders-ready' ||
            data.message === WORD_CHAIN_AD_PLACEHOLDERS_READY ||
            weightedQuizMessages.includes(data.message)
          ) {
            setBfpAdsMessages(prevState => {
              let messages = [...prevState];
              messages.push(data);
              return messages;
            });
          } else if (data.message === 'card-quiz-complete' && isEnabledInterstitialAdCardQuiz) {
            bfpQuizPlacer.current?.onGameInterstitialReady();
          } else if (!gameAction) {
            return;
          }

          if (bfpQuizPlacer.current) {
            bfpMessages.forEach(msg => {
              bfpQuizPlacer.current.onBFPReady(msg.messageData);
            });
            bfpMessages = [];
          }
        };

        if (t3App && typeof t3App.on === 'function') {
          t3App.on('message', onBfpT3Message);

          if (buzz.bfpFormats.includes('tap_on_image')) {
            window.BfpT3MessageReady = true;
            document.dispatchEvent(new Event('BfpT3MessageReady'));
          }
        }

        return () => {
          if (t3App && typeof t3App.off === 'function') {
            t3App.off('message', onBfpT3Message);
          }
        };
      },
      [t3App, bfpQuizPlacer, isEnabledInterstitialAdCardQuiz, handleGameInterstitialBroadcast] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
      let tmpTime = 10;
      const interval = setInterval(() => {
        tmpTime--;
        if (tmpTime <= 0) {
          clearInterval(interval);
          setBfpLoadIntervalFinished(true);
        }
      }, 1000);
      return () => clearInterval(interval);
    }, [bfpAdsMessages.length]);

    useEffect(() => {
      const countBfpsWithAds = buzz.sub_buzzes
        .filter(sb => sb.form === 'bfp' && BFP_WITH_ADS.indexOf(sb.format_name) !== -1)
        .length;
      // fire message only after recive messages from all bfps on page
      // or if noone was load for last 5 sec
      if ((bfpAdsMessages.length >= countBfpsWithAds || bfpLoadIntervalFinished) && bfpQuizPlacer.current) {
        bfpAdsMessages.sort((a, b) => {
          return (a?.messageData?.subbuzzIndex || 0) - (b?.messageData?.subbuzzIndex || 0);
        }).forEach(msg => {
          bfpQuizPlacer.current.onBFPReady(msg.messageData);
        });
      }
    }, [bfpAdsMessages, bfpQuizPlacer, buzz.id, buzz.sub_buzzes, bfpLoadIntervalFinished]);

    useEffect(() => {
      if (!experiments.loaded) {
        return () => {};
      }

      if (adsContext.status !== 'loaded') {
        return () => {};
      }

      let quizPlacer;
      let inlinePlacer;
      let presetDensity = 2;
      let skipNUnits = false;

      /**
     * LEGACY QUIZ INLINE LOGIC
     */
      // Inline ads for quizzes are handled separately then inline ads for other subbuzzes.
      if (buzz.is_quiz && !buzz.isBFPQuiz) {
        // RT-649: iOS crash incident
        // IGNORE sticky experiment - and set quiz ad density to less across the board
        // for mobile
        // first ad always after first quiz question; then after each 3 additional
        if (isNZorAU || highDensityQuiz) {
          presetDensity = 1;
        }

        // US mobile quiz users get ad every 2 questions (resolved from ads_quiz_experience test)
        if (userCountry === 'us' && isMobile()) {
          presetDensity = 2;
        }

        quizPlacer = createQuizInlinePlacer({
          AdUnit,
          buzz,
          disablePromo1Native,
          element: containerRef.current,
          onAdsReady: quizAdPortals =>
            setAdPortals(adPortals.concat(quizAdPortals)),
          pixiedust,
          presetDensity,
          skipNUnits,
        });
        quizPlacer.init();
      }

      /**
       * ARTICLE, ETC. INLINE LOGIC
       */
      inlinePlacer = createInlinePlacer({
        AdUnit: InlinePromoUnit,
        buzz,
        element: containerRef.current,
        onAdsReady: inlineAdPortals => {
          setAdPortals(adPortals.concat(inlineAdPortals));
        },
        pixiedust,
        abeagle: { experiments, getExperimentValue },
        skipNUnits: false,
        presetDensity: (() => {
          // increase density for shopping content in the US
          if (userCountry === 'us' && buzz.isShopping) {
            return 'density_shopping_increase';
          }
          //@TODO IN RT-1791: - MOVE EXPERIMENT LOGIC OUT

          if(rt1520IncreaseDensity !== 'control') {
            return 'density_' + rt1520IncreaseDensity;
          }

          if (isNZorAU || highDensityNonQuiz) {
            return 'density_200'
          }
          return false;
        })(),
        presetPlacements: (() => {
          // increase ad placements for shopping content in the US
          if (userCountry === 'us' && buzz.isShopping) {
            return 'placements_shopping_increase';
          }
          if(rt1520IncreaseDensity !== 'control') {
            return 'placements_' + rt1520IncreaseDensity;
          }

          if (isNZorAU || highDensityNonQuiz) {
            return 'placements_1'
          }
          return false;
        })(),
        looseTitles: (() => {
          // only loose titles on increase variant
          if(rt1520IncreaseDensity === 'increase') {
            return true;
          }
          return false;
        })(),
        densityLogic: highDensityForCampaign ? 'subbuzz': 'default',
      });
      inlinePlacer.init();

      return () => {
        if (inlinePlacer) {
          inlinePlacer.destroy();
        }
        if (quizPlacer) {
          quizPlacer.destroy();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buzz.id, adsContext, experiments.loaded]);

    /**
     * MISC BFP FORMAT INLINE HANDLER
     */
    useEffect(() => {
      if (!experiments.loaded) {
        return () => {};
      }

      if (adsContext.status !== 'loaded') {
        return () => {};
      }

      let presetDensity = 2;
      let skipNUnits = false;

      // if buzz has at least one bfp format create new AdBFPQuizManager
      if (!isEnableInlineAdCardQuiz && buzz.bfpFormats.length > 0) {
        // RT-649: iOS crash incident
        // IGNORE sticky experiment - and set quiz ad density to less across the board
        // for mobile
        // first ad always after first quiz question; then after each 3 additional
        if (isNZorAU || highDensityQuiz || rt1520IncreaseDensity === 'increase') {
          presetDensity = 1;
        }

        // US mobile quiz users get ad every 2 questions (resolved from ads_quiz_experience test)
        if (userCountry === 'us' && isMobile()) {
          presetDensity = 2;
        }

        bfpQuizPlacer.current = createBFPQuizManager({
          AdUnit,
          disablePromo1Native,
          element: containerRef.current,
          onAdsReady: inlineAdPortals =>
            setAdPortals(adPortals.concat(inlineAdPortals)),
          pixiedust,
          presetDensity,
          skipNUnits,
          eventBus: adsContext.eventBus,
        });
        bfpQuizPlacer.current.init();
      }

      // initialize BFPCardQuizManager if quiz format is card_quiz,
      // handle quiz progress and card question tracking
      if (isEnableInlineAdCardQuiz && buzz.bfpFormats.length > 0) {
        bfpQuizPlacer.current = createBFPCardQuizManager({
          AdUnit,
          disablePromo1Native,
          element: containerRef.current,
          onAdsReady: inlineAdPortals =>
            setAdPortals(adPortals.concat(inlineAdPortals)),
          pixiedust,
          cardQuizQuestionInterval: isInlineAdEligible ? 3 : 5,
          eventBus: adsContext.eventBus,
        });
        bfpQuizPlacer.current.init();
      }

      return () => {
        if (bfpQuizPlacer.current) {
          bfpQuizPlacer.current.destroy();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buzz.id, adsContext, experiments.loaded, isEnableInlineAdCardQuiz]);


    /**
     * Public Good's Action Unit (https://assets.publicgood.com/docs/gen/pgmApi.html)
    */
    useEffect(() => {
      if ( !isNews(buzz) || !containerRef?.current || window.pgmApi || !adPortals.length || adsContext.status === 'disabled' ) {
        return;
      }

      const publicGoodActionUnit = document.createElement('div');
      publicGoodActionUnit.setAttribute('id', 'publicGood');
      publicGoodActionUnit.classList.add('loading', 'clearfix', 'ad-ex');

      const pgmOptions = {
        partnerId: 'buzzfeed-buzzfeed-news',
      };
      if (CLUSTER === 'dev' || CLUSTER === 'stage') {
        pgmOptions.attributes = {
          targetId: '2d3d74ba-3807-4e28-875a-dfc5d67706f8',
          targetType: 'campaign',
        };
      }

      loadScript('https://assets.publicgood.com/pgm/v1/pgm-api.js', document.body).then(async (script) => {
        await script.onload();
        // render Public Good's Unit after mid inline Ad unit
        let midIndex = Math.floor(adPortals.length / 2);
        const nodes = containerRef.current.querySelectorAll('.js-ad-placement');
        nodes[midIndex].insertAdjacentElement('afterend', publicGoodActionUnit);

        window.pgmApi.create(publicGoodActionUnit, {
          ...pgmOptions,
          onShow: () => {
            publicGoodActionUnit.classList.remove('loading');
            const publicGoodTitle = document.createElement('h2');
            publicGoodTitle.classList.add('ad__disclosure--ex');
            publicGoodTitle.innerText = 'ARTICLE CONTINUES BELOW';
            publicGoodActionUnit.insertAdjacentElement('afterbegin', publicGoodTitle);
          },
          onHide: () => {
            window.pgmApi.remove();
            publicGoodActionUnit.remove();
          }
        });
      });
    }, [buzz, adsContext, adPortals, containerRef]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div ref={containerRef}>
        {buzz.classification.edition !== 'Japan' && !isGamesPost(buzz) && !isOrHasQuiz(buzz) && <ElementsPlayer buzz={buzz} />}
        <Component {...props} />
        {!hideAds && adPortals.map((AdPortal, idx) => (
          <AdPortal key={idx} />
        ))}
      </div>
    );
  };
};

export default withInlineAds;
